import * as THREE from 'three'

import type { components } from '../../schema'
import type { TransformedFacing, TransformedLayer, TransformedShelf } from '@state/shelf/types'

type Rotation = components['schemas']['Rotation']
export type Point3D = components['schemas']['Point3D']

export interface Point2D {
  x: number
  y: number
}

export interface Pose2D extends Point2D {
  rotationRad: number
}

type RotationMatrix = [
  [number, number, number],
  [number, number, number],
  [number, number, number]
]

export function getZRotationFromQuaternionRad(quaternion: Rotation) {
  const sinr_cosp = 2 * (quaternion.w * quaternion.z + quaternion.x * quaternion.y)
  const cosr_cosp = 1 - 2 * (quaternion.y * quaternion.y + quaternion.z * quaternion.z)
  return Math.atan2(sinr_cosp, cosr_cosp)
}

export function getZRotationFromQuaternionDeg(quaternion: Rotation) {
  return getZRotationFromQuaternionRad(quaternion) * (180 / Math.PI)
}

export function quaternionToRotationMatrix(q: Rotation): RotationMatrix {
  const { x, y, z, w } = q

  return [
    [
      1 - 2 * y * y - 2 * z * z,
      2 * x * y - 2 * z * w,
      2 * x * z + 2 * y * w,
    ],
    [
      2 * x * y + 2 * z * w,
      1 - 2 * x * x - 2 * z * z,
      2 * y * z - 2 * x * w,
    ],
    [
      2 * x * z - 2 * y * w,
      2 * y * z + 2 * x * w,
      1 - 2 * x * x - 2 * y * y,
    ],
  ]
}

export function globalToLocal(point: Point3D, origin: Point3D, rotationMatrix: RotationMatrix): Point3D {
  const dx = point.x - origin.x
  const dy = point.y - origin.y
  const dz = point.z - origin.z

  return {
    x: rotationMatrix[0][0] * dx + rotationMatrix[1][0] * dy + rotationMatrix[2][0] * dz,
    y: rotationMatrix[0][1] * dx + rotationMatrix[1][1] * dy + rotationMatrix[2][1] * dz,
    z: rotationMatrix[0][2] * dx + rotationMatrix[1][2] * dy + rotationMatrix[2][2] * dz,
  }
}

export function localToGlobal(point: Point3D, origin: Point3D, rotationMatrix: RotationMatrix): Point3D {
  return {
    x: origin.x + rotationMatrix[0][0] * point.x + rotationMatrix[0][1] * point.y + rotationMatrix[0][2] * point.z,
    y: origin.y + rotationMatrix[1][0] * point.x + rotationMatrix[1][1] * point.y + rotationMatrix[1][2] * point.z,
    z: origin.z + rotationMatrix[2][0] * point.x + rotationMatrix[2][1] * point.y + rotationMatrix[2][2] * point.z,
  }
}

interface ShelfContext {
  shelf: TransformedShelf
  rotationMatrix: RotationMatrix
  quaternion: THREE.Quaternion
}

interface LayerContext extends ShelfContext {
  layer: TransformedLayer
}

interface FacingContext extends LayerContext {
  facing: TransformedFacing
}

export interface FacingGeometry {
  x: number
  y: number
  z: number
  width: number
  height: number
}

interface LayerGeometry {
  position: Point3D
  width: number
  height: number
  depth: number
}

export interface FacingWithGeometry {
  facingId: string
  geometry: FacingGeometry
  quaternion: THREE.Quaternion
}

export function buildFacingGeometry(ctx: FacingContext): FacingWithGeometry {
  const { facing, layer, shelf, rotationMatrix, quaternion } = ctx

  const shelfOrigin = { x: shelf.x, y: shelf.y, z: 0 }

  const width = facing.width * shelf.width
  const height = layer.height * shelf.height

  const x = facing.left * shelf.width + width / 2 // <-- compensate for the fact that these are bottom left coordinates and three places at the center
  const z = layer.y * shelf.height + height / 2 // <-- compensate for the fact that these are bottom left coordinates and three places at the center

  // the overall layout has been flipped along the Y axis, so we need to position the facing at shelf.depth
  // otherwise it would be on the back side of the shelf
  const center = localToGlobal({ x, y: shelf.depth, z }, shelfOrigin, rotationMatrix)

  return {
    facingId: facing.id,
    geometry: {
      x: center.x,
      y: center.y,
      z: center.z,
      width,
      height,
    },
    quaternion,
  }
}

export interface LayerWithGeometry {
  layerId: string
  geometry: LayerGeometry
  quaternion: THREE.Quaternion
}

export function buildLayerGeometry(ctx: LayerContext): LayerWithGeometry {
  const { layer, shelf, quaternion } = ctx

  const layerHeight = 0.02
  const verticalOffset = layer.y * shelf.height + layerHeight / 2 // center of the layer box vertically

  const position = {
    x: shelf.center.x,
    y: shelf.center.y,
    z: shelf.center.z + verticalOffset,
  }

  return {
    layerId: layer.id,
    geometry: {
      position,
      width: shelf.width,
      height: layerHeight,
      depth: shelf.depth,
    },
    quaternion,
  }
}
